import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, AlertCircle, ArrowRight, Clock } from 'lucide-react';
import axios from '../axiosConfig';
import './KYCProgressCard.css';

/**
 * KYC progress card.
 *
 * Shows the merchant their verification level + a single clear next step
 * (CTA button when actionable, info pill when waiting on review, rejection
 * notice with reason when rejected). The earlier verbose "Unlock next
 * level to get:" benefits list was dropped — that information lives on
 * the /kyc page; here on Overview, less noise = clearer signal.
 */
const KYCProgressCard = () => {
  const [kycData, setKycData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      const response = await axios.get('/kyc/status');
      if (response.data.success) {
        setKycData(response.data.kyc);
      }
    } catch (err) {
      console.error('Failed to fetch KYC status:', err);
    } finally {
      setLoading(false);
    }
  };

  // Level → display label + transaction limit + theme color.
  // Color drives the icon tint and level badge bg.
  const getLevelInfo = (level) => {
    const levels = {
      'LEVEL_0': { label: 'Not started',         limit: 'Verify to accept payments', color: '#DC2626' },
      'LEVEL_1': { label: 'Basic verification',  limit: '₦100,000 daily limit',      color: '#F59E0B' },
      'LEVEL_2': { label: 'Identity verified',   limit: '₦500,000 daily limit',      color: '#2563EB' },
      'LEVEL_3': { label: 'Full verification',   limit: 'Unlimited transactions',    color: '#16A34A' },
    };
    return levels[level] || levels['LEVEL_0'];
  };

  const getStatusMessage = (status) => {
    const messages = {
      'NOT_STARTED':            'Verify your business to start accepting payments.',
      'IN_PROGRESS':            'Continue verification to unlock payment features.',
      'PENDING_REVIEW':         'Your documents are under review.',
      'UNDER_REVIEW':           'Our team is reviewing your documents.',
      'APPROVED':               'Verified — all features unlocked.',
      'REJECTED':               'Verification was unsuccessful. Please review and resubmit.',
      'RESUBMISSION_REQUIRED':  'Please update your information and resubmit.',
    };
    return messages[status] || messages['NOT_STARTED'];
  };

  if (loading || !kycData) return null;
  // Hide entirely once verification is complete.
  if (kycData.progressPercentage >= 100) return null;

  const levelInfo = getLevelInfo(kycData.level);
  const isComplete = kycData.status === 'APPROVED' && kycData.level === 'LEVEL_3';
  const isPending = ['PENDING_REVIEW', 'UNDER_REVIEW'].includes(kycData.status);
  const needsAction = ['NOT_STARTED', 'IN_PROGRESS', 'RESUBMISSION_REQUIRED'].includes(kycData.status);
  const levelNumber = kycData.level.replace('LEVEL_', '');

  return (
    <div className={`kyc-progress-card ${isComplete ? 'complete' : ''} ${isPending ? 'pending' : ''}`}>
      <div className="kyc-card-header">
        <div className="kyc-card-title-section">
          <div
            className="kyc-icon-wrapper"
            style={{ backgroundColor: `${levelInfo.color}1F`, color: levelInfo.color }}
          >
            {isComplete ? <CheckCircle /> : <Shield />}
          </div>
          <div>
            <h3 className="kyc-card-title">KYC verification</h3>
            <p className="kyc-card-subtitle">{getStatusMessage(kycData.status)}</p>
          </div>
        </div>
        <div className="kyc-level-badge" style={{ backgroundColor: levelInfo.color }}>
          Level {levelNumber}/3
        </div>
      </div>

      <div className="kyc-progress-section">
        <div className="kyc-progress-header">
          <span className="kyc-progress-label">Progress</span>
          <span className="kyc-progress-percentage">{kycData.progressPercentage}%</span>
        </div>
        <div className="kyc-progress-bar-container">
          <div
            className="kyc-progress-bar-fill"
            style={{
              width: `${kycData.progressPercentage}%`,
              backgroundColor: levelInfo.color
            }}
          />
        </div>
        <p className="kyc-progress-status">{levelInfo.label} · {levelInfo.limit}</p>
      </div>

      {isPending && (
        <div className="kyc-pending-notice">
          <Clock size={14} />
          <span>Review typically takes 2–24 hours. We'll email you once approved.</span>
        </div>
      )}

      {kycData.status === 'REJECTED' && kycData.rejectionReason && (
        <div className="kyc-rejection-notice">
          <AlertCircle size={14} />
          <div>
            <strong>Rejection reason</strong>
            <p>{kycData.rejectionReason}</p>
          </div>
        </div>
      )}

      {needsAction && (
        <button
          type="button"
          className="kyc-action-button"
          onClick={() => navigate('/kyc')}
        >
          {kycData.progressPercentage === 0 ? 'Start verification' : 'Continue verification'}
          <ArrowRight size={16} />
        </button>
      )}

      {isComplete && (
        <div className="kyc-complete-badge">
          <CheckCircle size={16} />
          <span>Fully verified — all features unlocked</span>
        </div>
      )}
    </div>
  );
};

export default KYCProgressCard;
